import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { AccountHeader } from '@mobi/component-library/Account'
import {
  AlertBox,
  Button,
  Icon,
  Input,
  FeatureIcon,
  InfoBox,
} from '@mobi/component-library/Common/V2'
import { DocumentPermission } from '@mobi/account/Areas/SignUp/Components/Verification'
import { DocumentBodyStyled, FormLayout } from './Layout.styled'
import { spacing } from '@mobi/component-library/Theme/Common'
import { statesInAustralia } from '@mobi/settings/statesInAustralia'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import {
  ALPHANUMERIC_REGEX,
  getRequiredValidationMessage,
  isValidName,
  isVerificationSuccessful,
  routes,
} from '@mobi/account/Areas/SignUp/Utils'
import {
  type TypeDriversLicenceDetailsRequest,
  type TypeSignUpApplicationResponse,
  useVerifyDriversLicence,
} from '../../Hooks'
import { useDispatch, useSelector } from 'react-redux'
import {
  getSignUpState,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
} from '../../Store'
import { SignupError } from '../SignupError'
import { Contract } from '@mobi/web-native-comms'
import { ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT } from '../../Utils'
import {
  trackVerificationEnterIdentification,
  trackVerificationSuccess,
} from '@mobi/account/Areas/Verification/Utils/ga'
import { trackSignUpError } from '../../Utils/ga'
import { unwrapErrorMessage } from '@mobi/utils/errors'
import { NameInput } from '@mobi/account/Common'

interface DriversLicenceProps {
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
}

export const DriversLicence = ({ trackOptimoveEvent }: DriversLicenceProps) => {
  const [showCardNumberInfoModal, setShowCardNumberInfoModal] = useState(false)
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const [state, setState] = React.useState<string | null>('')
  const [number, setNumber] = React.useState('')
  const [cardNumber, setCardNumber] = React.useState('')

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const signupApplication = useSelector(getSignUpState)?.signupApplication
  const { verifyDriversLicence, verifyDriversLicenceStatus } = useVerifyDriversLicence()

  const [firstName, setFirstName] = React.useState('')
  const [middleName, setMiddleName] = React.useState('')
  const [surname, setSurname] = React.useState('')

  const allowToProceed =
    givePermission &&
    !!state &&
    !!number &&
    !!cardNumber &&
    isValidName(firstName) &&
    isValidName(middleName, true) &&
    isValidName(surname)

  const onSubmit = () => {
    if (
      !signupApplication ||
      !signupApplication.applicationReferenceNumber ||
      !signupApplication.enquiryId ||
      !allowToProceed
    ) {
      return
    }
    const driversLicence: TypeDriversLicenceDetailsRequest = {
      birthdate: signupApplication.birthdate,
      firstName: firstName,
      surname: surname,
      middleName: middleName,
      email: signupApplication.email,
      mobileNumber: signupApplication.mobileNumber,
      residentialAddress: signupApplication.residentialAddress,
      previousAddress: signupApplication.previousAddress,
      enquiryId: signupApplication.enquiryId,
      applicationReferenceNumber: signupApplication.applicationReferenceNumber,
      licenceDetails: {
        number,
        state,
        cardNumber,
      },
      marketingOptInIndicator: signupApplication.marketingOptInIndicator,
    }
    dispatch(setSignupApplication({ ...signupApplication, firstName, middleName, surname }))
    verifyDriversLicence(driversLicence, {
      onSuccess: verification => {
        if (signupApplication.applicationReferenceNumber != null) {
          const verificationResponse: TypeSignUpApplicationResponse = {
            applicationReferenceNumber: signupApplication.applicationReferenceNumber,
            enquiryId: verification.enquiryId,
            duplicateExists: false,
            isDuplicateClosed: false,
            isVerified: verification.isVerified,
            unVerifiedReason: verification.unVerifiedReason,
            potentialFraudMatch: verification.potentialFraudMatch,
            potentialSanctionedMatch: verification.potentialSanctionedMatch,
          }
          dispatch(setSignupVerification(verificationResponse))
          const isSuccessful = isVerificationSuccessful(verificationResponse)

          trackVerificationEnterIdentification('licence', isSuccessful)
          if (isSuccessful) {
            trackVerificationSuccess('Verified Identification')
            trackOptimoveEvent(ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT)
            navigate(routes.password)
            return
          }

          dispatch(setNumberOfAttemptsToVerify())
          if (
            verificationResponse.duplicateExists ||
            verificationResponse.potentialSanctionedMatch ||
            !verificationResponse.isVerified
          ) {
            navigate(routes.signUpUnsuccessful)
            return
          }
        }
      },
      onError: error => {
        trackSignUpError(location.pathname, unwrapErrorMessage(error))
        setShowErrorPopup(true)
      },
    })
  }

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  if (!signupApplication) {
    return null
  }

  return (
    <>
      <FormLayout>
        <AccountHeader
          alignItems='start'
          title='Enter your Driver’s Licence details'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your details as they appear on your ID document.
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <DocumentBodyStyled>
          <NameInput
            firstName={firstName}
            middleName={middleName}
            lastName={surname}
            setFirstName={setFirstName}
            setMiddleName={setMiddleName}
            setLastName={setSurname}
            isDisabled={verifyDriversLicenceStatus === 'pending'}
          />
          <DropdownInput
            name='state'
            label='State'
            placeholder='Details must match your ID'
            value={state || ''}
            isDisabled={verifyDriversLicenceStatus === 'pending'}
            onChange={event => {
              setState(event.target.value || '')
              resetFieldValidation('state')
            }}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                state: getRequiredValidationMessage(state),
              }))
            }}
            isInvalid={!!validationMessages.state}
            helpText={validationMessages.state}
          >
            {statesInAustralia.map(state => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </DropdownInput>
          <Input
            name='licenceNumber'
            label={`Driver's licence number`}
            placeholder='Details must match your ID'
            isDisabled={verifyDriversLicenceStatus === 'pending'}
            value={number}
            onChange={e => setNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
            onFocus={() => resetFieldValidation('licenceNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                licenceNumber: getRequiredValidationMessage(number),
              }))
            }}
            isInvalid={!!validationMessages.licenceNumber}
            helpText={validationMessages.licenceNumber}
          />
          <Input
            name='cardNumber'
            label={`Driver's licence card number`}
            placeholder='Details must match your ID'
            isDisabled={verifyDriversLicenceStatus === 'pending'}
            value={cardNumber}
            onChange={e => setCardNumber(e.target.value.replace(ALPHANUMERIC_REGEX, ''))}
            onFocus={() => resetFieldValidation('cardNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                cardNumber: getRequiredValidationMessage(cardNumber),
              }))
            }}
            isInvalid={!!validationMessages.cardNumber}
            helpText={validationMessages.cardNumber}
            suffix={
              <Icon
                name='LineInfoCircle'
                size='2rem'
                onClick={() => setShowCardNumberInfoModal(true)}
                data-testid='drivers-licence-info'
              />
            }
          />
          <div style={{ paddingTop: spacing.smx1 }}>
            <DocumentPermission
              isBusy={verifyDriversLicenceStatus === 'pending'}
              onChange={e => setGivePermission(e.target.checked)}
            />
          </div>
        </DocumentBodyStyled>
        <footer>
          <Button
            isFullWidth
            disabled={!allowToProceed}
            isLoading={verifyDriversLicenceStatus === 'pending'}
            onClick={() => onSubmit()}
          >
            Continue
          </Button>
        </footer>
        <AlertBox
          title='Driver’s Licence Card Number'
          text="A 'card number' is now a mandatory verification field for Australian driver's licences. Depending on your state, this may be found on either the front or back of the card."
          icon={
            <FeatureIcon type='warning' size='lg'>
              <Icon name='LineAlertTriangle' size='2.4rem' />
            </FeatureIcon>
          }
          confirmText='Ok'
          confirmColor='primary'
          isOpen={showCardNumberInfoModal}
          onConfirm={() => setShowCardNumberInfoModal(false)}
          alignLeft
        />
      </FormLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
